import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, User, Lock, ShieldCheck, Send, AlertCircle, Building2, Bug } from "lucide-react";

// Show developer-only utilities when we're not in a real production build.
const IS_DEV_ENV = process.env.REACT_APP_ENV !== "production";

export default function OwnerSettings() {
  const { user } = useAuth();
  const [showPw, setShowPw] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastSend, setLastSend] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/owner/companies")
      .then(({ data }) => {
        if (cancelled) return;
        setCompanies(data);
        if (data.length && !companyId) setCompanyId(data[0].id); // default to first
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load companies"))
      .finally(() => { if (!cancelled) setLoadingCompanies(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendPreview = async () => {
    if (!companyId) { toast.error("Pick a company first"); return; }
    setSending(true);
    try {
      const payload = { company_id: companyId };
      if (testTo.trim()) payload.to = testTo.trim();
      const { data } = await api.post("/notifications/preview", payload);
      setLastSend(data);
      toast.success(`Digest sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-4 md:p-10 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl">OWNER SETTINGS</h1>
        <p className="text-sm text-muted-foreground mt-2">Identity, security, and per-company notifications.</p>
      </div>

      {/* Account */}
      <div className="brutal-card p-6 md:p-8 space-y-5">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-5 h-5 text-safety-yellow" strokeWidth={3} />
          <h2 className="font-heading text-xl">ACCOUNT</h2>
        </div>
        <Field icon={User} label="Name" value={user?.name || "Platform Owner"} />
        <Field icon={Mail} label="Email" value={user?.email} hint="Immutable primary identifier" mono />
        <div className="pt-3">
          <button data-testid="change-password-btn" onClick={() => setShowPw(true)} className="btn-primary w-full">
            <Lock className="w-5 h-5 mr-2" strokeWidth={3} /> Change Password
          </button>
        </div>
      </div>

      {/* Notifications */}
      <div className="brutal-card p-6 md:p-8 space-y-5">
        <div className="flex items-center gap-2 mb-2">
          <Send className="w-5 h-5 text-safety-yellow" strokeWidth={3} />
          <h2 className="font-heading text-xl">CERTIFICATION REPORT EMAIL</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Sends a branded digest of every certification that is <b>expired or expiring within 30 days</b> for the
          selected company. Scoped strictly to that company&apos;s roster.
        </p>

        <div>
          <label className="block font-bold uppercase text-xs tracking-wider mb-2 inline-flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" strokeWidth={3} /> Company
          </label>
          <select
            data-testid="digest-company"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            disabled={loadingCompanies || !companies.length}
            className="industrial-input"
          >
            {loadingCompanies && <option value="">Loading…</option>}
            {!loadingCompanies && companies.length === 0 && <option value="">No companies</option>}
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name} · {c.employee_count} employees</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block font-bold uppercase text-xs tracking-wider mb-2 inline-flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" strokeWidth={3} /> Recipient
          </label>
          <input
            data-testid="digest-recipient"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="Leave blank to use the configured test address"
            autoComplete="email"
            className="industrial-input"
          />
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground/80 mt-1">
            Default: <b>NOTIFICATIONS_TEST_RECIPIENT</b> from backend env
          </div>
        </div>

        <button
          data-testid="send-digest-btn"
          onClick={sendPreview}
          disabled={sending || !companyId}
          className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Send className="w-5 h-5 mr-2" strokeWidth={3} />
          {sending ? "Sending…" : "Send Certification Report Now"}
        </button>

        {lastSend && (
          <div data-testid="last-send-summary" className="border-2 border-safety-yellow bg-safety-yellow/10 p-4 space-y-1 text-xs">
            <div className="font-bold uppercase tracking-wider text-safety-yellow">Last Send</div>
            <div><b>Scope:</b> {lastSend.scope}</div>
            <div><b>To:</b> <span className="font-mono">{lastSend.sent_to}</span></div>
            <div><b>Subject:</b> {lastSend.subject}</div>
            <div className="flex flex-wrap gap-3 mt-2">
              <Pill label="Expired"      value={lastSend.counts?.expired} bg="bg-status-expired" fg="text-white" />
              <Pill label="Expiring 7d"  value={lastSend.counts?.expiring_7} bg="bg-status-expiring" fg="text-black" />
              <Pill label="Expiring 30d" value={lastSend.counts?.expiring_30} bg="bg-safety-yellow" fg="text-black" />
            </div>
            <div className="mt-2 text-muted-foreground">Resend ID: <span className="font-mono">{lastSend.resend?.id || "—"}</span></div>
          </div>
        )}

        <div className="inline-flex items-start gap-2 text-[11px] text-muted-foreground">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-safety-yellow" strokeWidth={3} />
          <span>
            Until the sender domain (<b>karmsafety.app</b>) is verified in Resend, emails can only be delivered
            to your Resend account address. Verify DNS records to unlock sending to any admin.
          </span>
        </div>
      </div>

      {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}

      {IS_DEV_ENV && (
        <div className="brutal-card p-6 md:p-8 space-y-4 border-status-expired">
          <div className="flex items-center gap-2 mb-2">
            <Bug className="w-5 h-5 text-status-expired" strokeWidth={3} />
            <h2 className="font-heading text-xl">DEVELOPER TOOLS</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Hidden in production. Use to verify that crash reports reach Sentry.
          </p>
          <button
            data-testid="sentry-test-crash-btn"
            onClick={() => {
              throw new Error("Sentry test crash — triggered from Owner Settings");
            }}
            className="btn-primary w-full bg-status-expired hover:bg-status-expired/90"
          >
            <Bug className="w-5 h-5 mr-2" strokeWidth={3} /> Trigger Test Crash
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, value, hint, mono }) {
  return (
    <div>
      <label className="block font-bold uppercase text-xs tracking-wider mb-2 inline-flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" strokeWidth={3} /> {label}
      </label>
      <div className={`industrial-input bg-muted text-muted-foreground cursor-not-allowed select-text ${mono ? "font-mono text-sm" : ""}`}>
        {value || "—"}
      </div>
      {hint && <div className="text-[10px] uppercase tracking-wider text-muted-foreground/80 mt-1">{hint}</div>}
    </div>
  );
}

function Pill({ label, value, bg, fg }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-widest border-2 border-black ${bg} ${fg}`}>
      <b>{value ?? 0}</b> {label}
    </span>
  );
}
